import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type { BusinessUnit, Project, System } from '../api/types';
import { CreateBuModal, CreateSystemModal } from './org-modals';

/**
 * Settings → Business units and systems.
 *
 * Every asset belongs to a system, and the asset form and CSV import only accept systems inside
 * the project's scope. A new organisation has none, so this is where a user creates them before
 * adding assets: business units (Company-level projects) and systems under each business unit.
 */
export function OrgStructurePanel({ project }: { project: Project }) {
  const auth = useAuth();
  const qc = useQueryClient();
  const canCreate = auth.hasRole('admin', 'analyst');
  const [modal, setModal] = useState<{ kind: 'bu' } | { kind: 'system'; buId: string } | null>(null);

  const companyId = project.scope_company_id;
  const busQ = useQuery({
    queryKey: ['business-units', companyId],
    queryFn: () => api.get<BusinessUnit[]>(`/business-units?company_id=${companyId}`),
    enabled: !!companyId,
  });
  const sysQ = useQuery({
    queryKey: ['systems-by-company', companyId],
    queryFn: () => api.get<System[]>(`/systems?company_id=${companyId}`),
    enabled: !!companyId,
  });

  // Business units and systems inside the project's scope.
  const bus = (busQ.data ?? []).filter((b) =>
    project.project_level === 'Company' ? true : b.id === project.scope_business_unit_id,
  );
  const systems = (sysQ.data ?? []).filter((s) =>
    project.project_level === 'System' ? s.id === project.scope_system_id : true,
  );
  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['business-units'] });
    void qc.invalidateQueries({ queryKey: ['systems-by-company'] });
    void qc.invalidateQueries({ queryKey: ['systems'] });
    void qc.invalidateQueries({ queryKey: ['project-systems'] });
  };

  if (!companyId) return null;
  return (
    <section className="mt-8" aria-labelledby="org-structure-h">
      <div className="flex items-baseline justify-between">
        <h3 id="org-structure-h" className="font-display text-lg text-ink-900">Business units and systems</h3>
        {canCreate && project.project_level === 'Company' && (
          <button type="button" className="btn-ghost text-sm" onClick={() => setModal({ kind: 'bu' })}>
            + Business unit
          </button>
        )}
      </div>
      <p className="text-sm text-ink-500 mt-1">
        Every asset belongs to a system. Create the systems here, then add assets on the Assets tab (by
        form or CSV, where <code>system_name</code> must match one of these names or short codes).
      </p>

      {busQ.isLoading || sysQ.isLoading ? (
        <p className="text-sm text-ink-400 mt-3">Loading…</p>
      ) : bus.length === 0 ? (
        <p className="text-sm text-ink-600 mt-3" data-testid="org-empty">
          No business units yet. {canCreate && project.project_level === 'Company' ? 'Create one to start adding systems.' : ''}
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-ink-100 border border-ink-100 rounded" data-testid="org-structure">
          {[...bus].sort(byName).map((b) => {
            const mine = systems.filter((s) => s.business_unit_id === b.id).sort(byName);
            return (
              <li key={b.id} className="p-3">
                <div className="flex items-baseline justify-between">
                  <div>
                    <span className="font-medium text-ink-900">{b.name}</span>
                    <span className="text-xs text-ink-400 ml-2 font-mono">{b.short_code}</span>
                  </div>
                  {canCreate && project.project_level !== 'System' && (
                    <button type="button" className="btn-ghost text-xs" onClick={() => setModal({ kind: 'system', buId: b.id })}>
                      + System
                    </button>
                  )}
                </div>
                {mine.length === 0 ? (
                  <p className="text-xs text-ink-400 mt-1">No systems yet.</p>
                ) : (
                  <ul className="mt-1 flex flex-wrap gap-2">
                    {mine.map((s) => (
                      <li key={s.id} className="text-xs bg-ink-50 border border-ink-100 rounded px-2 py-0.5">
                        {s.name} <span className="text-ink-400 font-mono">{s.short_code}</span> · {s.system_type.replace('_', ' ')}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {modal?.kind === 'bu' && (
        <CreateBuModal companyId={companyId} onClose={() => setModal(null)} onCreated={() => { setModal(null); refresh(); }} />
      )}
      {modal?.kind === 'system' && (
        <CreateSystemModal buId={modal.buId} onClose={() => setModal(null)} onCreated={() => { setModal(null); refresh(); }} />
      )}
    </section>
  );
}
